import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import _ from 'lodash';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { useCardOrder } from '@/contexts/cardOrderContext';
import useRoleSettings from '@/hooks/useRoleSettings';
import useUserSetting from '@/hooks/useUserSetting';
import { clearSetting, getPersonalSetting, subscribe } from '@/services/userSettingsStore';
import { CardOrderSettingKey } from '@/types/userSettings';
import reconcileCardOrder, {
  OrderableCard,
  sortHiddenLast,
  toCardOrder
} from '@/utils/reconcileCardOrder';

interface Params<T extends OrderableCard> {
  /** The page's cards in the order they ship in. */
  cards: T[];
  settingKey: CardOrderSettingKey;
}

/**
 * Owns the order of a page's sortable cards, in both of the orders a page can
 * show: the reader's own, and the default being edited for a role.
 *
 * The two are kept apart on purpose. Previewing or editing a role's default
 * never touches the reader's own order, so backing out of the editor leaves
 * them exactly where they were.
 *
 * The controls that drive all this live in the page's toolbar rather than on
 * the page, so they are published to the card order context instead of being
 * returned. What the page gets back is the cards to render (hidden ones
 * last), the drag, toggle and hide handlers, and the banner shown while a
 * role's default is being drafted.
 */
const usePageCardOrder = <T extends OrderableCard>({ cards, settingKey }: Params<T>) => {
  const [savedOrder, setSavedOrder] = useUserSetting(settingKey, []);
  const { canEdit, isSaving, myRoles, roles, saveRoleSettings, settingsForRole } =
    useRoleSettings();
  const { publish } = useCardOrder();

  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [draftOrder, setDraftOrder] = useState<T[] | null>(null);

  // Whether the reader has an order of their own, as opposed to sitting on
  // their role's default. Read straight from the store so resetting back to
  // the default re-renders.
  const hasPersonalOrder = useSyncExternalStore(
    subscribe,
    () => getPersonalSetting(settingKey) !== undefined,
    () => getPersonalSetting(settingKey) !== undefined
  );

  const personalCards = reconcileCardOrder(savedOrder, cards);
  const roleCards = reconcileCardOrder(settingsForRole(editingRole)[settingKey], cards);
  const draftCards = draftOrder ?? roleCards;

  const isEditingRole = editingRole !== null;
  const isDirty = isEditingRole && !_.isEqual(toCardOrder(draftCards), toCardOrder(roleCards));

  const visibleCards = isEditingRole ? draftCards : personalCards;

  // The role whose layout the reader is currently on, so the menu can show
  // which one is in use. Matched by value: a reader following a default and a
  // reader who happens to have dragged their way to the same order are on the
  // same layout, and there is nothing else stored to tell them apart.
  const currentOrder = toCardOrder(personalCards);
  const matchingRole =
    roles.find(({ frontend_settings: settings }) =>
      _.isEqual(toCardOrder(reconcileCardOrder(settings[settingKey], cards)), currentOrder)
    )?.slug ?? null;

  const setVisibleCards = (cards: T[]) => {
    const next = sortHiddenLast(cards);

    if (isEditingRole) {
      setDraftOrder(next);
      return;
    }

    setSavedOrder(toCardOrder(next));
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const oldIndex = visibleCards.findIndex(({ id }) => id === active.id);
    const newIndex = visibleCards.findIndex(({ id }) => id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    setVisibleCards(arrayMove(visibleCards, oldIndex, newIndex));
  };

  const onToggle = (cardId: string) => {
    setVisibleCards(
      visibleCards.map((card) =>
        card.id === cardId ? { ...card, defaultOpen: !card.defaultOpen } : card
      )
    );
  };

  // Hiding goes through the same order as a drag, so a role's default can
  // ship with cards already hidden for everyone following it.
  const setHidden = (cardId: string, hidden: boolean) => {
    setVisibleCards(visibleCards.map((card) => (card.id === cardId ? { ...card, hidden } : card)));
  };

  // Staff who work in more than one role see more than one default layout, so
  // they get to pick which of theirs to follow even when they may not edit any
  // role's default. The pick is applied to their own order rather than
  // previewed: without a save button there would be nothing to make it last.
  const applyRoleOrder = (slug: string) =>
    setSavedOrder(toCardOrder(reconcileCardOrder(settingsForRole(slug)[settingKey], cards)));

  const editRole = (slug: string | null) => {
    setEditingRole(slug);
    setDraftOrder(null);
  };

  const discardDraft = () => setDraftOrder(null);

  const saveDraft = async () => {
    if (!editingRole) return;

    await saveRoleSettings({
      role: editingRole,
      settings: { [settingKey]: toCardOrder(draftCards) }
    });
    setDraftOrder(null);
  };

  const resetToRoleDefault = () => clearSetting(settingKey);

  // Republished on every render so the menu's handlers always close over this
  // render's cards. The provider only re-renders when the snapshot changes by
  // value, so this does not loop.
  useEffect(() => {
    publish({
      snapshot: {
        canEdit,
        editingRole,
        hasPersonalOrder,
        // Worth showing to an editor, and to anyone who works in more than one
        // role and so has more than one default layout to choose between.
        isAvailable: canEdit || myRoles.length > 1,
        isDirty,
        isSaving,
        matchingRole,
        myRoles,
        roles
      },
      handlers: { applyRoleOrder, discardDraft, editRole, resetToRoleDefault, saveDraft }
    });
  });

  // The menu belongs to whichever page is on screen, so it goes when this one does.
  useEffect(() => () => publish(null), [publish]);

  return {
    cards: visibleCards,
    draftBar: {
      discardDraft,
      editingRoleName: roles.find(({ slug }) => slug === editingRole)?.name,
      exitEditing: () => editRole(null),
      isDirty,
      isSaving,
      saveDraft
    },
    onDragEnd,
    onHide: (cardId: string) => setHidden(cardId, true),
    onToggle,
    onUnhide: (cardId: string) => setHidden(cardId, false)
  };
};

export default usePageCardOrder;
